import { validateMapSet } from './map-cache.js';
import type { ServerMessage } from './protocol.js';
import { PROTOCOL_VERSION } from './protocol-version.js';
import { isValidPublicRoom } from './public-room-validation.js';
import {
  isValidDynamicTrackEventSnapshot,
  isValidImpactSnapshot,
  isValidProjectileSnapshot,
  isValidRacerSnapshot,
  isValidTrafficOffsets,
} from './snapshot-codec.js';
import type { MapDefinition } from './types.js';

const SERVER_MESSAGE_TYPES = new Set([
  'welcome',
  'spectator-welcome',
  'room-state',
  'snapshot',
  'frame',
  'frame-delta',
  'player-joined',
  'player-left',
  'host-changed',
  'connection-replaced',
  'post-race-vote',
  'party-transfer',
  'pong',
  'latency',
  'error',
]);

export class ProtocolMismatchError extends Error {
  constructor() {
    super('Server protocol version does not match this client');
    this.name = 'ProtocolMismatchError';
  }
}

export function parseServerMessage(data: unknown): ServerMessage {
  return parseServerMessageValue(JSON.parse(String(data)) as unknown);
}

/** Shared runtime boundary for WebSocket and local-worker server messages. */
export function parseServerMessageValue(value: unknown): ServerMessage {
  if (!isRecord(value) || typeof value.type !== 'string' || !SERVER_MESSAGE_TYPES.has(value.type)) {
    throw new Error('Unsupported server message');
  }
  // Read the version envelope before the version-specific body. A newer
  // server may change that body and should trigger a clean update hand-off.
  if (
    (
      (value.type === 'welcome' || value.type === 'spectator-welcome')
      && Number.isInteger(value.protocolVersion)
      && value.protocolVersion !== PROTOCOL_VERSION
    )
    || (
      (value.type === 'frame' || value.type === 'frame-delta')
      && Number.isInteger(value.v)
      && value.v !== PROTOCOL_VERSION
    )
  ) {
    throw new ProtocolMismatchError();
  }
  switch (value.type) {
    case 'welcome':
    case 'spectator-welcome':
      if (
        typeof value.protocolVersion !== 'number'
        || !Number.isInteger(value.protocolVersion)
        || !isValidPublicRoom(value.room)
        || !validMap(value.map)
        || value.room.settings.mapId !== value.map.id
        || !Array.isArray(value.racers)
        || value.racers.length < 1
        || value.racers.length > 32
        || !value.racers.every(isValidRacerSnapshot)
        || (
          value.type === 'welcome'
          && (
            !identifier(value.playerId)
            || typeof value.isHost !== 'boolean'
            || !value.racers.some((racer) => racer.id === value.playerId)
          )
        )
      ) {
        throw new Error('Malformed welcome message');
      }
      break;
    case 'player-joined':
      if (!isValidRacerSnapshot(value.racer)) throw new Error('Malformed player join message');
      break;
    case 'player-left':
      if (!identifier(value.playerId)) throw new Error('Malformed player leave message');
      break;
    case 'host-changed':
      if (!identifier(value.hostPlayerId)) throw new Error('Malformed host message');
      break;
    case 'post-race-vote': {
      if (
        !isRecord(value.votes)
        || !integerInRange(value.required, 1, 16)
        || !(value.resolved === null
          || ['rematch', 'next-map', 'surprise'].includes(String(value.resolved)))
      ) throw new Error('Malformed post-race vote');
      for (const [vote, count] of Object.entries(value.votes)) {
        if (
          !['rematch', 'next-map', 'surprise'].includes(vote)
          || !integerInRange(count, 0, 16)
        ) throw new Error('Malformed post-race vote');
      }
      break;
    }
    case 'party-transfer':
      if (
        !isValidPublicRoom(value.room)
        || typeof value.inviteToken !== 'string'
        || !(value.inviteToken === '' || /^[a-zA-Z0-9_-]{8,200}$/.test(value.inviteToken))
      ) throw new Error('Malformed party transfer');
      break;
    case 'pong':
      if (
        typeof value.sentAt !== 'number'
        || !Number.isFinite(value.sentAt)
        || typeof value.serverTime !== 'number'
        || !Number.isFinite(value.serverTime)
      ) throw new Error('Malformed pong message');
      break;
    case 'latency': {
      const pings = value.pings;
      if (
        !pings || typeof pings !== 'object' || Array.isArray(pings)
        || Object.keys(pings).length > 32
        || Object.values(pings).some((ping) => typeof ping !== 'number' || !Number.isFinite(ping) || ping < 0)
      ) throw new Error('Malformed latency message');
      break;
    }
    case 'error':
      if (typeof value.code !== 'string' || typeof value.message !== 'string') {
        throw new Error('Malformed error message');
      }
      break;
    case 'room-state':
      if (!isValidPublicRoom(value.room)) throw new Error('Malformed room message');
      break;
    case 'snapshot':
      if (
        !nonNegativeInteger(value.sequence)
        || !finite(value.serverTime)
        || !['lobby', 'countdown', 'racing', 'finished'].includes(String(value.phase))
        || !finite(value.countdownMs)
        || !(value.raceTimeRemainingMs === undefined
          || value.raceTimeRemainingMs === null
          || finite(value.raceTimeRemainingMs))
        || !(value.trafficTimeMs === undefined || finite(value.trafficTimeMs))
        || !Array.isArray(value.racers)
        || value.racers.length > 32
        || !value.racers.every(isValidRacerSnapshot)
        || !Array.isArray(value.projectiles)
        || value.projectiles.length > 128
        || !value.projectiles.every(isValidProjectileSnapshot)
        || !Array.isArray(value.impacts)
        || value.impacts.length > 256
        || !value.impacts.every(isValidImpactSnapshot)
        || !isValidTrafficOffsets(value.trafficOffsets)
        || !isValidDynamicTrackEventSnapshot(value.dynamicTrackEvent)
      ) throw new Error('Malformed snapshot message');
      break;
    default:
      // Frame bodies are validated and reconstructed by RaceFrameDecoder.
      break;
  }
  return value as unknown as ServerMessage;
}

export function isServerMessage(value: unknown): value is ServerMessage {
  try {
    parseServerMessageValue(value);
    return true;
  } catch {
    return false;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validMap(value: unknown): value is MapDefinition {
  return validateMapSet([value]) !== null;
}

function identifier(value: unknown): value is string {
  return typeof value === 'string' && /^[a-zA-Z0-9_-]{1,100}$/.test(value);
}

function finite(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function nonNegativeInteger(value: unknown): boolean {
  return Number.isInteger(value) && Number(value) >= 0;
}

function integerInRange(value: unknown, minimum: number, maximum: number): boolean {
  return Number.isInteger(value) && Number(value) >= minimum && Number(value) <= maximum;
}
